// pos_local sinxronlaşma vəziyyəti.
//
// Mənbə: `GET /v1/sync/status` (pos_local). Cloud-a deyil, YERLİ backend-ə
// müraciət olunur — sinxronlaşmanı aparan odur və vəziyyəti yalnız o bilir.
//
// NİYƏ LAZIMDIR: POS açılanda (xüsusən cihaz təzə qeydiyyatdan keçəndə) menyu,
// masalar və personal buluddan çəkilir. Bu bir neçə saniyə çəkir və o müddətdə
// ekran boş görünür. İşçi üçün bu "proqram işləmir" deməkdir — halbuki sadəcə
// məlumat yoldadır.

import { POS_ENABLED } from './config';
import { posRequest } from './posClient';

export interface SyncStatus {
  /** 'http' = buluda qoşulub · 'mock' = cihaz qeydiyyatdan keçməyib */
  cloudClient: 'http' | 'mock' | string;
  breaker: unknown;
  /** Buluda göndərilməyi gözləyən yerli hadisələr. */
  outbox: Record<string, number>;
  pull: {
    cursors: Record<string, number>;
    lastPulledAt: Record<string, number>;
    /** Hazırda çəkilən stream-lər; boş deyilsə sinxronlaşma gedir. */
    active: string[];
    applied: Record<string, number>;
    completed: string[];
    total: number;
    /** İlk tam sinxron hələ bitməyib. */
    initial: boolean;
    /** Son uğursuz pull — səbəb istifadəçiyə göstərilir. */
    lastError: { stream: string; message: string; at: number } | null;
    /** Bulud cihazı tanımır (token admin paneldə silinib). */
    authRevoked?: boolean;
    authRevokedReason?: string | null;
    /** Bulud WS kanalı qoşuludur — dəyişikliklər ani gəlir. */
    realtime?: boolean;
  };
}

/** Cihaz buluda qoşulubmu (konfiqurasiya var və CLOUD_CLIENT=http). */
export function isCloudLinked(status: SyncStatus | null): boolean {
  return status?.cloudClient === 'http';
}

/** Hazırda məlumat çəkilirmi. */
export function isSyncing(status: SyncStatus | null): boolean {
  return (status?.pull.active.length ?? 0) > 0;
}

/**
 * İlk sinxronun tərəqqisi 0..1.
 *
 * Faiz stream sayı ilə ölçülür (catalog / floorplan / staff / live_orders),
 * event sayı ilə yox: bulud "ümumi neçə event var" demir, yalnız `hasMore`
 * qaytarır. Uydurma faiz göstərməkdənsə dürüst ölçü seçilib.
 */
export function initialProgress(status: SyncStatus | null): number {
  if (!status || status.pull.total === 0) return 0;
  return Math.min(1, status.pull.completed.length / status.pull.total);
}

/** Bu proses ərzində tətbiq olunmuş event sayı — işin getdiyini göstərir. */
export function appliedCount(status: SyncStatus | null): number {
  if (!status) return 0;
  return Object.values(status.pull.applied).reduce((sum, n) => sum + (n || 0), 0);
}

/** Buluda göndərilməyi gözləyən hadisə sayı (oflayn işləyəndə artır). */
export function pendingOutbox(status: SyncStatus | null): number {
  if (!status) return 0;
  return Number(status.outbox?.pending ?? 0);
}

/** Son sinxron xətası (varsa) — ekranda göstərmək üçün. */
export function syncError(status: SyncStatus | null): string | null {
  return status?.pull?.lastError?.message ?? null;
}

/**
 * Cihaz buludda ləğv olunub.
 *
 * Adi sinxron xətasından AYRICA göstərilir, çünki işçinin görəcəyi iş
 * başqadır: internet gözləmək kömək etməyəcək, panelə müraciət lazımdır.
 */
export function isDeviceRevoked(status: SyncStatus | null): boolean {
  return status?.pull?.authRevoked === true;
}

export async function getSyncStatus(): Promise<SyncStatus | null> {
  if (!POS_ENABLED) return null;
  const res = await posRequest<SyncStatus>({ method: 'GET', path: '/v1/sync/status' });
  return res.ok && res.data ? res.data : null;
}
